import { useState, useEffect, useCallback } from 'react';

/*
  useDiscordAuth — manages Discord OAuth2 login state.

  Flow:
    1. loginWithDiscord() → redirects to Discord OAuth
    2. Discord redirects back to /auth/callback?code=...
    3. AuthCallback page picks up the code, calls /.netlify/functions/discord-callback
    4. Function exchanges code → access token → fetches user → joins server
    5. User object is stored in sessionStorage so it survives page reloads
*/

const STORAGE_KEY = 'perm_discord_user';

export function useDiscordAuth() {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  // Restore user from session storage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) setUser(JSON.parse(stored));
    } catch {}
  }, []);

  const loginWithDiscord = useCallback(() => {
    const clientId    = process.env.REACT_APP_DISCORD_CLIENT_ID;
    const redirectUri = encodeURIComponent('https://www.permsystems.com/auth/callback');
    const scope       = encodeURIComponent('identify email guilds.join');
    const url =
      `https://discord.com/api/oauth2/authorize` +
      `?client_id=${clientId}` +
      `&redirect_uri=${redirectUri}` +
      `&response_type=code` +
      `&scope=${scope}`;
    window.location.href = url;
  }, []);

  const handleCallback = useCallback(async (code) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/.netlify/functions/discord-callback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        // Show the full raw response so we can debug
        const detail = data.detail || data.error || JSON.stringify(data);
        throw new Error(`${detail}`);
      }
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data.user));
      setUser(data.user);
      return { user: data.user, joined: data.joined };
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setUser(null);
    setError(null);
  }, []);

  return { user, loading, error, loginWithDiscord, handleCallback, logout };
}
